import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Sparkles, MapPin, Check, Loader2, ArrowRight, ArrowLeft, RefreshCw, Utensils, Clock, Globe, Thermometer, Wind, Cloud, Sun, CheckCircle, AlertTriangle, Shirt, Download, Share2, Users, Baby, Calendar, Wallet, Train, Plane, Hotel, UtensilsCrossed, MapPinned } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import SEO from '../components/SEO';

// jsPDF is loaded via CDN in index.html
const { jsPDF } = window.jspdf || {};

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AIPlannerPage = () => {
  const [searchParams] = useSearchParams();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    destination: '',
    duration: '7',
    starting_location: '',
    adults: 2,
    children: 0,
    budget: 'medium',
    travel_type: 'relaxed',
    travel_month: '',
    interests: []
  });
  const [isLoading, setIsLoading] = useState(false);
  const [planGenerated, setPlanGenerated] = useState(false);
  const [aiResponse, setAiResponse] = useState(null);
  const [error, setError] = useState(null);

  // Pre-fill destination from URL params (from homepage search)
  useEffect(() => {
    const destinationParam = searchParams.get('destination');
    if (destinationParam) {
      setFormData(prev => ({ ...prev, destination: destinationParam }));
    }
  }, [searchParams]);

  const interests = [
    { id: 'sightseeing', label: 'City Sightseeing', icon: '🏙️', description: 'Urban exploration' },
    { id: 'landmarks', label: 'Iconic Landmarks', icon: '🗼', description: 'Famous monuments' },
    { id: 'adventure', label: 'Adventure & Outdoors', icon: '🏔️', description: 'Thrilling activities' },
    { id: 'art', label: 'Art & Museums', icon: '🎨', description: 'Cultural exhibits' },
    { id: 'heritage', label: 'Culture & Heritage', icon: '🏛️', description: 'Historic sites' },
    { id: 'family', label: 'Family and Fun', icon: '👨‍👩‍👧‍👦', description: 'Kid-friendly activities' },
    { id: 'food', label: 'Food & Dining', icon: '🍽️', description: 'Local cuisine' },
    { id: 'nightlife', label: 'Nightlife', icon: '🌃', description: 'Entertainment' },
    { id: 'nature', label: 'Nature & Wildlife', icon: '🌿', description: 'Natural beauty' },
    { id: 'beach', label: 'Beach & Water', icon: '🏖️', description: 'Coastal activities' },
    { id: 'shopping', label: 'Shopping', icon: '🛍️', description: 'Markets & malls' },
    { id: 'wellness', label: 'Spa & Wellness', icon: '💆', description: 'Relaxation' }
  ];

  const popularDestinations = [
    { name: 'Kerala', flag: '🇮🇳' },
    { name: 'Goa', flag: '🇮🇳' },
    { name: 'Rajasthan', flag: '🇮🇳' },
    { name: 'Himachal Pradesh', flag: '🇮🇳' },
    { name: 'Kashmir', flag: '🇮🇳' },
    { name: 'Varanasi', flag: '🇮🇳' },
    { name: 'Andaman', flag: '🇮🇳' },
    { name: 'North East', flag: '🇮🇳' }
  ];

  const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

  const budgetOptions = [
    { id: 'low', label: 'Budget', icon: '💰', description: 'Cost-effective' },
    { id: 'medium', label: 'Mid-Range', icon: '💎', description: 'Comfortable' },
    { id: 'luxury', label: 'Luxury', icon: '👑', description: 'Premium experience' }
  ];

  const travelStyles = [
    { id: 'relaxed', label: 'Relaxed', description: 'Easy pace' },
    { id: 'adventure', label: 'Adventure', description: 'Action packed' },
    { id: 'spiritual', label: 'Spiritual', description: 'Soul searching' },
    { id: 'family', label: 'Family', description: 'Kid-friendly' }
  ];

  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleInterest = (id) => {
    setFormData(prev => ({
      ...prev,
      interests: prev.interests.includes(id) 
        ? prev.interests.filter(i => i !== id) 
        : [...prev.interests, id]
    }));
  };

  const handleGeneratePlan = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_URL}/api/ai/plan-trip`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          destination: formData.destination,
          duration: parseInt(formData.duration) || 7,
          travel_type: formData.travel_type,
          budget: formData.budget,
          interests: formData.interests,
          starting_location: formData.starting_location,
          adults: formData.adults,
          children: formData.children,
          travel_month: formData.travel_month
        }),
      });
      if (!response.ok) throw new Error('Failed to generate trip plan');
      const data = await response.json();
      setAiResponse(data);
      setPlanGenerated(true);
      setCurrentStep(4);
    } catch (err) {
      console.error('Error generating plan:', err);
      setError('Failed to generate trip plan. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setCurrentStep(1);
    setFormData({
      destination: '',
      duration: '7',
      starting_location: '',
      adults: 2,
      children: 0,
      budget: 'medium',
      travel_type: 'relaxed',
      travel_month: '',
      interests: []
    });
    setPlanGenerated(false);
    setAiResponse(null);
    setError(null);
  };

  const handleDownloadItinerary = () => {
    if (!aiResponse) return;

    // Create PDF document (A4 size)
    const doc = new jsPDF('p', 'mm', 'a4');
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 20;
    const contentWidth = pageWidth - (margin * 2);
    let y = margin;

    // Colors
    const primaryColor = [139, 90, 43]; // Brown
    const accentColor = [181, 137, 75]; // Gold
    const textColor = [51, 51, 51];
    const mutedColor = [128, 128, 128];

    // Helper function to check page break
    const checkPageBreak = (requiredSpace) => {
      if (y + requiredSpace > pageHeight - margin) {
        doc.addPage();
        y = margin;
        return true;
      }
      return false;
    };

    // Helper function to add wrapped text
    const addWrappedText = (text, x, maxWidth, fontSize, color = textColor) => {
      doc.setFontSize(fontSize);
      doc.setTextColor(...color);
      const lines = doc.splitTextToSize(text, maxWidth);
      lines.forEach(line => {
        checkPageBreak(fontSize * 0.5);
        doc.text(line, x, y);
        y += fontSize * 0.45;
      });
      return lines.length;
    };

    // === HEADER / COVER ===
    // Header background
    doc.setFillColor(...primaryColor);
    doc.rect(0, 0, pageWidth, 55, 'F');

    // Company Name
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(28);
    doc.setTextColor(255, 255, 255);
    doc.text('SENTORA HOLIDAYS', margin, 25);

    // Tagline
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(220, 220, 220);
    doc.text('a Unit of Sam Tours and Travels Pvt Ltd | Est. 1985', margin, 33);

    // Trip Title
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.setTextColor(255, 255, 255);
    doc.text(`${aiResponse.destination.toUpperCase()} - ${aiResponse.duration} DAY ITINERARY`, margin, 47);

    y = 70;

    // === TRIP SUMMARY BOX ===
    doc.setFillColor(250, 248, 245);
    doc.roundedRect(margin, y, contentWidth, 35, 3, 3, 'F');
    doc.setDrawColor(...accentColor);
    doc.roundedRect(margin, y, contentWidth, 35, 3, 3, 'S');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(...primaryColor);
    doc.text('TRIP OVERVIEW', margin + 5, y + 8);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(...textColor);
    
    const summaryItems = [
      `Destination: ${aiResponse.destination}`,
      `Duration: ${aiResponse.duration} Days`,
      `Travelers: ${formData.adults} Adults${formData.children > 0 ? `, ${formData.children} Children` : ''}`,
      `Travel Style: ${formData.travel_type.charAt(0).toUpperCase() + formData.travel_type.slice(1)}`,
      `Budget: ${formData.budget.charAt(0).toUpperCase() + formData.budget.slice(1)}`,
      formData.travel_month ? `Travel Month: ${formData.travel_month}` : ''
    ].filter(Boolean);

    const col1 = summaryItems.slice(0, 3);
    const col2 = summaryItems.slice(3);
    
    col1.forEach((item, i) => {
      doc.text(item, margin + 5, y + 16 + (i * 5));
    });
    col2.forEach((item, i) => {
      doc.text(item, margin + contentWidth/2, y + 16 + (i * 5));
    });

    y += 45;

    // === TRANSPORT INFO ===
    if (aiResponse.transport_info) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      doc.setTextColor(...primaryColor);
      doc.text('TRANSPORT INFORMATION', margin, y);
      y += 7;

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(...textColor);
      
      const transportInfo = [
        `Nearest Airport: ${aiResponse.transport_info.nearest_airport || 'N/A'}`,
        `Nearest Railway: ${aiResponse.transport_info.nearest_railway || 'N/A'}`,
        `Local Transport: ${aiResponse.transport_info.local_transport || 'N/A'}`
      ];
      
      transportInfo.forEach(info => {
        doc.text(`• ${info}`, margin + 3, y);
        y += 5;
      });
      y += 5;
    }

    // === DAY-WISE ITINERARY ===
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.setTextColor(...primaryColor);
    checkPageBreak(20);
    doc.text('DAY-WISE ITINERARY', margin, y);
    y += 3;
    
    // Underline
    doc.setDrawColor(...accentColor);
    doc.setLineWidth(0.5);
    doc.line(margin, y, margin + 50, y);
    y += 10;

    (aiResponse.itinerary || []).forEach((day) => {
      checkPageBreak(25);
      
      // Day header box
      doc.setFillColor(...primaryColor);
      doc.roundedRect(margin, y - 4, 25, 10, 2, 2, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.setTextColor(255, 255, 255);
      doc.text(`DAY ${day.day}`, margin + 3, y + 2);

      // Day title
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.setTextColor(...textColor);
      doc.text(day.title || '', margin + 30, y + 2);
      y += 12;

      // Activities
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      (day.activities || []).forEach(activity => {
        checkPageBreak(8);
        doc.setTextColor(...accentColor);
        doc.text('•', margin + 5, y);
        doc.setTextColor(...textColor);
        const actLines = doc.splitTextToSize(activity, contentWidth - 15);
        actLines.forEach((line, idx) => {
          doc.text(line, margin + 10, y);
          y += 4.5;
        });
      });

      // Meals & Travel time
      if (day.meals || day.travel_time) {
        checkPageBreak(8);
        doc.setFontSize(8);
        doc.setTextColor(...mutedColor);
        let infoLine = [];
        if (day.meals) infoLine.push(`Meals: ${day.meals}`);
        if (day.travel_time) infoLine.push(`Travel: ${day.travel_time}`);
        doc.text(infoLine.join('  |  '), margin + 5, y);
        y += 6;
      }
      y += 5;
    });

    // === TOP ATTRACTIONS ===
    if (aiResponse.top_attractions?.length > 0) {
      checkPageBreak(25);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      doc.setTextColor(...primaryColor);
      doc.text('TOP ATTRACTIONS', margin, y);
      y += 8;

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(...textColor);
      aiResponse.top_attractions.forEach((attr, i) => {
        checkPageBreak(6);
        doc.text(`${i + 1}. ${attr}`, margin + 3, y);
        y += 5;
      });
      y += 5;
    }

    // === HOTEL SUGGESTIONS ===
    if (aiResponse.hotel_suggestions) {
      checkPageBreak(30);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      doc.setTextColor(...primaryColor);
      doc.text('HOTEL SUGGESTIONS', margin, y);
      y += 8;

      const hotelCategories = [
        { key: 'budget', label: 'Budget Options' },
        { key: 'midrange', label: 'Mid-Range Options' },
        { key: 'premium', label: 'Premium Options' }
      ];

      hotelCategories.forEach(cat => {
        const hotels = aiResponse.hotel_suggestions[cat.key];
        if (hotels?.length > 0) {
          checkPageBreak(15);
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(9);
          doc.setTextColor(...accentColor);
          doc.text(cat.label, margin + 3, y);
          y += 5;

          doc.setFont('helvetica', 'normal');
          doc.setTextColor(...textColor);
          hotels.forEach(hotel => {
            checkPageBreak(6);
            doc.text(`• ${hotel}`, margin + 6, y);
            y += 4.5;
          });
          y += 3;
        }
      });
      y += 3;
    }

    // === LOCAL FOOD ===
    if (aiResponse.local_food?.length > 0) {
      checkPageBreak(25);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      doc.setTextColor(...primaryColor);
      doc.text('LOCAL CUISINE TO TRY', margin, y);
      y += 8;

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(...textColor);
      aiResponse.local_food.forEach(food => {
        checkPageBreak(6);
        doc.text(`• ${food}`, margin + 3, y);
        y += 5;
      });
      y += 5;
    }

    // === PACKING LIST ===
    if (aiResponse.packing_list?.length > 0) {
      checkPageBreak(25);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      doc.setTextColor(...primaryColor);
      doc.text('PACKING CHECKLIST', margin, y);
      y += 8;

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(...textColor);
      
      const packingItems = aiResponse.packing_list;
      const colWidth = contentWidth / 2;
      const col1Items = packingItems.slice(0, Math.ceil(packingItems.length / 2));
      const col2Items = packingItems.slice(Math.ceil(packingItems.length / 2));
      
      const maxRows = Math.max(col1Items.length, col2Items.length);
      for (let i = 0; i < maxRows; i++) {
        checkPageBreak(6);
        if (col1Items[i]) {
          doc.text(`☐ ${col1Items[i]}`, margin + 3, y);
        }
        if (col2Items[i]) {
          doc.text(`☐ ${col2Items[i]}`, margin + colWidth + 3, y);
        }
        y += 5;
      }
      y += 5;
    }

    // === TRAVEL TIPS ===
    if (aiResponse.travel_tips?.length > 0) {
      checkPageBreak(25);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      doc.setTextColor(...primaryColor);
      doc.text('TRAVEL TIPS', margin, y);
      y += 8;

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(...textColor);
      aiResponse.travel_tips.forEach((tip, i) => {
        checkPageBreak(10);
        const tipLines = doc.splitTextToSize(`${i + 1}. ${tip}`, contentWidth - 5);
        tipLines.forEach(line => {
          doc.text(line, margin + 3, y);
          y += 4.5;
        });
        y += 2;
      });
      y += 3;
    }

    // === LOCAL CUSTOMS ===
    if (aiResponse.local_customs?.length > 0) {
      checkPageBreak(25);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      doc.setTextColor(...primaryColor);
      doc.text('LOCAL CUSTOMS & ETIQUETTE', margin, y);
      y += 8;

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(...textColor);
      aiResponse.local_customs.forEach(custom => {
        checkPageBreak(8);
        const customLines = doc.splitTextToSize(`• ${custom}`, contentWidth - 5);
        customLines.forEach(line => {
          doc.text(line, margin + 3, y);
          y += 4.5;
        });
      });
    }

    // === FOOTER ON ALL PAGES ===
    const totalPages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      
      // Footer line
      doc.setDrawColor(...accentColor);
      doc.setLineWidth(0.3);
      doc.line(margin, pageHeight - 18, pageWidth - margin, pageHeight - 18);
      
      // Footer text
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(...mutedColor);
      doc.text('Sentora Holidays | www.sentoraholidays.com | tours@sentoraholidays.com', margin, pageHeight - 12);
      doc.text(`Page ${i} of ${totalPages}`, pageWidth - margin - 20, pageHeight - 12);
      
      // Generation note
      doc.setFontSize(7);
      doc.text(`Generated on ${new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })} | This is an AI-generated itinerary for reference purposes.`, margin, pageHeight - 8);
    }

    // Save PDF
    doc.save(`Sentora_${aiResponse.destination.replace(/[^a-zA-Z0-9]/g, '_')}_${aiResponse.duration}Day_Itinerary.pdf`);
  };

  const handleContactWithItinerary = () => {
    if (!aiResponse) return;
    
    // Generate full itinerary content (similar to download but formatted for message)
    let fullPlan = `═══════════════════════════════════════════\n`;
    fullPlan += `AI TRIP PLAN: ${aiResponse.destination}\n`;
    fullPlan += `═══════════════════════════════════════════\n\n`;
    
    fullPlan += `BOOKING REQUEST DETAILS\n`;
    fullPlan += `───────────────────────────────────────────\n`;
    fullPlan += `Destination: ${aiResponse.destination}\n`;
    fullPlan += `Duration: ${aiResponse.duration} days\n`;
    fullPlan += `Adults: ${formData.adults}\n`;
    fullPlan += `Children: ${formData.children}\n`;
    fullPlan += `Budget Level: ${formData.budget}\n`;
    fullPlan += `Travel Style: ${formData.travel_type}\n`;
    fullPlan += `Starting Location: ${formData.starting_location || 'Not specified'}\n`;
    fullPlan += `Travel Month: ${formData.travel_month || 'Flexible'}\n`;
    fullPlan += `Interests: ${formData.interests.join(', ')}\n\n`;

    if (aiResponse.transport_info) {
      fullPlan += `TRANSPORT INFO\n`;
      fullPlan += `───────────────────────────────────────────\n`;
      fullPlan += `Nearest Airport: ${aiResponse.transport_info.nearest_airport || 'N/A'}\n`;
      fullPlan += `Nearest Railway: ${aiResponse.transport_info.nearest_railway || 'N/A'}\n`;
      fullPlan += `Local Transport: ${aiResponse.transport_info.local_transport || 'N/A'}\n\n`;
    }

    fullPlan += `DAY-WISE ITINERARY\n`;
    fullPlan += `───────────────────────────────────────────\n`;
    (aiResponse.itinerary || []).forEach(day => {
      fullPlan += `\nDay ${day.day}: ${day.title}\n`;
      (day.activities || []).forEach(act => fullPlan += `  • ${act}\n`);
      if (day.meals) fullPlan += `  Meals: ${day.meals}\n`;
      if (day.travel_time) fullPlan += `  Travel Time: ${day.travel_time}\n`;
    });

    if (aiResponse.top_attractions?.length > 0) {
      fullPlan += `\n\nTOP ATTRACTIONS\n`;
      fullPlan += `───────────────────────────────────────────\n`;
      aiResponse.top_attractions.forEach((attr, i) => fullPlan += `${i + 1}. ${attr}\n`);
    }

    if (aiResponse.hotel_suggestions) {
      fullPlan += `\nHOTEL SUGGESTIONS\n`;
      fullPlan += `───────────────────────────────────────────\n`;
      if (aiResponse.hotel_suggestions.budget?.length > 0) {
        fullPlan += `Budget Options:\n`;
        aiResponse.hotel_suggestions.budget.forEach(h => fullPlan += `  • ${h}\n`);
      }
      if (aiResponse.hotel_suggestions.midrange?.length > 0) {
        fullPlan += `Mid-Range Options:\n`;
        aiResponse.hotel_suggestions.midrange.forEach(h => fullPlan += `  • ${h}\n`);
      }
      if (aiResponse.hotel_suggestions.premium?.length > 0) {
        fullPlan += `Premium Options:\n`;
        aiResponse.hotel_suggestions.premium.forEach(h => fullPlan += `  • ${h}\n`);
      }
    }

    if (aiResponse.local_food?.length > 0) {
      fullPlan += `\nLOCAL FOOD TO TRY\n`;
      fullPlan += `───────────────────────────────────────────\n`;
      aiResponse.local_food.slice(0, 5).forEach(food => fullPlan += `• ${food}\n`);
    }

    if (aiResponse.family_notes && formData.children > 0) {
      fullPlan += `\nFAMILY & CHILD NOTES\n`;
      fullPlan += `───────────────────────────────────────────\n`;
      fullPlan += `${aiResponse.family_notes}\n`;
    }

    fullPlan += `\n═══════════════════════════════════════════\n`;
    fullPlan += `Generated by Sentora Holidays AI Trip Planner\n`;
    fullPlan += `Please customize this plan as per my requirements.\n`;
    
    // Navigate to contact page with full plan
    const subject = `AI Trip Plan Booking - ${aiResponse.destination} (${aiResponse.duration} days)`;
    window.location.href = `/contact?message=${encodeURIComponent(fullPlan)}&subject=${encodeURIComponent(subject)}`;
  };

  const getWeatherDescription = (code) => {
    const weatherCodes = {
      0: 'Clear sky', 1: 'Mainly clear', 2: 'Partly cloudy', 3: 'Overcast',
      45: 'Foggy', 51: 'Light drizzle', 61: 'Slight rain', 63: 'Moderate rain',
      65: 'Heavy rain', 80: 'Slight rain showers', 95: 'Thunderstorm'
    };
    return weatherCodes[code] || 'Variable';
  };

  const canProceedStep1 = formData.destination.trim().length > 0;
  const canProceedStep2 = true; // All fields optional in step 2
  const canProceedStep3 = formData.interests.length > 0;

  return (
    <div className="min-h-screen bg-s-bg">
      <SEO 
        title="AI Trip Planner - Plan Your Perfect India Vacation | Sentora Holidays" 
        description="Plan your perfect India trip with AI-powered recommendations. Get personalized day-by-day itineraries, hotel suggestions, and local tips based on your preferences, budget, and travel style."
        keywords="AI trip planner, India vacation planner, travel itinerary generator, personalized travel plan, India holiday planner, trip planning tool"
        url="https://www.sentoraholidays.com/ai-planner"
      />

      {/* Hero */}
      <section className="relative h-[30vh] md:h-[40vh] overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=1920&q=80)' }} />
        <div className="absolute inset-0 bg-gradient-to-r from-s-bg/95 via-s-bg/85 to-s-bg/75" />
        <div className="relative z-10 h-full flex items-center justify-center">
          <div className="text-center px-4 max-w-3xl">
            <Badge className="bg-gradient-to-r from-s-primary to-s-primary text-white border-0 mb-3 px-3 py-1.5 text-xs md:text-sm" data-testid="ai-badge">
              <Sparkles size={14} className="mr-1.5" /> AI-Powered Trip Planner
            </Badge>
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-s-text mb-3 font-heritage italic">
              Plan Your Dream Trip
            </h1>
            <p className="text-sm md:text-base text-s-text2 max-w-2xl mx-auto">
              Answer a few questions and let our AI create a personalized itinerary based on Sentora Holidays' curated travel experiences
            </p>
          </div>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-10">
        {!planGenerated ? (
          <div className="space-y-6">
            {/* Progress Steps */}
            <div className="flex items-center justify-center gap-1 md:gap-3 mb-6">
              {[1, 2, 3].map((step) => (
                <React.Fragment key={step}>
                  <div className="flex items-center gap-1.5">
                    <div className={`w-8 h-8 md:w-10 md:h-10 rounded-full flex items-center justify-center font-bold transition-all text-sm md:text-base ${
                      currentStep >= step ? 'bg-s-primary text-white' : 'bg-s-card text-s-muted'
                    }`}>
                      {currentStep > step ? <Check size={16} /> : step}
                    </div>
                    <span className={`text-xs md:text-sm hidden sm:block ${currentStep >= step ? 'text-s-text' : 'text-s-muted'}`}>
                      {step === 1 ? 'Where' : step === 2 ? 'Details' : 'Interests'}
                    </span>
                  </div>
                  {step < 3 && <div className={`h-0.5 w-8 md:w-16 ${currentStep > step ? 'bg-s-primary' : 'bg-s-border'}`} />}
                </React.Fragment>
              ))}
            </div>

            {error && (
              <div className="bg-s-primary/20 border border-red-500/30 text-s-primary px-4 py-3 rounded-lg flex items-center gap-2">
                <AlertTriangle size={18} />
                <span>{error}</span>
              </div>
            )}

            {/* Step 1: Destination */}
            {currentStep === 1 && (
              <Card className="bg-s-bg2 border-s-border shadow-lg">
                <CardHeader className="border-b border-s-border pb-4">
                  <CardTitle className="flex items-center gap-2 text-xl md:text-2xl text-s-text">
                    <MapPin className="w-5 h-5 md:w-6 md:h-6 text-s-primary" />
                    Where would you like to go?
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-5 md:p-8 space-y-6">
                  <div>
                    <Label className="text-s-text text-base font-medium mb-2 block">Destination *</Label>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 text-s-muted" size={20} />
                      <Input
                        data-testid="destination-input"
                        value={formData.destination}
                        onChange={(e) => updateFormData('destination', e.target.value)}
                        placeholder="e.g., Kerala, Goa, Rajasthan..."
                        className="pl-10 h-12 md:h-14 bg-s-card border-s-border text-s-text text-base placeholder:text-s-muted rounded-xl"
                      />
                    </div>
                  </div>

                  <div>
                    <Label className="text-s-text text-base font-medium mb-3 block">Popular Destinations</Label>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                      {popularDestinations.map((dest) => (
                        <button
                          key={dest.name}
                          onClick={() => updateFormData('destination', dest.name)}
                          className={`flex items-center gap-2 p-3 rounded-xl border transition-all text-left ${
                            formData.destination === dest.name 
                              ? 'border-s-primary bg-s-primary/10' 
                              : 'border-s-border hover:border-s-primary bg-s-card hover:bg-s-card/80'
                          }`}
                        >
                          <span className="text-xl">{dest.flag}</span>
                          <span className="text-s-text text-xs md:text-sm font-medium truncate">{dest.name}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <Label className="text-s-text text-base font-medium mb-3 block">How many days?</Label>
                    <div className="flex flex-wrap gap-2">
                      {['3', '5', '7', '10', '14'].map((d) => (
                        <Button
                          key={d}
                          data-testid={`duration-${d}-btn`}
                          variant={formData.duration === d ? 'default' : 'outline'}
                          onClick={() => updateFormData('duration', d)}
                          className={`flex-1 min-w-[70px] h-11 text-sm ${
                            formData.duration === d 
                              ? 'bg-s-primary hover:bg-s-primary/90 text-white border-0' 
                              : 'border-s-border text-s-text2 hover:text-s-text'
                          }`}
                        >
                          {d} Days
                        </Button>
                      ))}
                    </div>
                  </div>

                  <Button
                    onClick={() => setCurrentStep(2)}
                    disabled={!canProceedStep1}
                    className="w-full h-12 md:h-14 text-base bg-gradient-to-r from-s-primary to-s-primary hover:opacity-90 rounded-xl font-bold"
                    data-testid="step1-next-btn"
                  >
                    Next: Trip Details <ArrowRight className="ml-2" size={20} />
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Step 2: Trip Details */}
            {currentStep === 2 && (
              <Card className="bg-s-bg2 border-s-border shadow-lg">
                <CardHeader className="border-b border-s-border pb-4">
                  <CardTitle className="flex items-center gap-2 text-xl md:text-2xl text-s-text">
                    <Users className="w-5 h-5 md:w-6 md:h-6 text-s-primary" />
                    Tell us more about your trip
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-5 md:p-8 space-y-6">
                  {/* Travelers */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-s-text text-sm font-medium mb-2 flex items-center gap-2">
                        <Users size={16} /> Adults
                      </Label>
                      <div className="flex items-center gap-2">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => updateFormData('adults', Math.max(1, formData.adults - 1))}
                          className="border-s-border"
                        >-</Button>
                        <span className="w-12 text-center text-s-text font-semibold text-lg">{formData.adults}</span>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => updateFormData('adults', formData.adults + 1)}
                          className="border-s-border"
                        >+</Button>
                      </div>
                    </div>
                    <div>
                      <Label className="text-s-text text-sm font-medium mb-2 flex items-center gap-2">
                        <Baby size={16} /> Children
                      </Label>
                      <div className="flex items-center gap-2">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => updateFormData('children', Math.max(0, formData.children - 1))}
                          className="border-s-border"
                        >-</Button>
                        <span className="w-12 text-center text-s-text font-semibold text-lg">{formData.children}</span>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => updateFormData('children', formData.children + 1)}
                          className="border-s-border"
                        >+</Button>
                      </div>
                    </div>
                  </div>

                  {/* Starting Location */}
                  <div>
                    <Label className="text-s-text text-sm font-medium mb-2 block">Starting Location (Optional)</Label>
                    <Input
                      value={formData.starting_location}
                      onChange={(e) => updateFormData('starting_location', e.target.value)}
                      placeholder="e.g., Mumbai, Delhi, Bangalore..."
                      className="h-11 bg-s-card border-s-border text-s-text placeholder:text-s-muted rounded-xl"
                    />
                  </div>

                  {/* Travel Month */}
                  <div>
                    <Label className="text-s-text text-sm font-medium mb-2 flex items-center gap-2">
                      <Calendar size={16} /> Travel Month (Optional)
                    </Label>
                    <div className="grid grid-cols-4 md:grid-cols-6 gap-2">
                      {months.map((month) => (
                        <button
                          key={month}
                          onClick={() => updateFormData('travel_month', formData.travel_month === month ? '' : month)}
                          className={`p-2 rounded-lg text-xs font-medium transition-all ${
                            formData.travel_month === month
                              ? 'bg-s-primary text-white'
                              : 'bg-s-card border border-s-border text-s-text2 hover:border-s-primary'
                          }`}
                        >
                          {month.slice(0, 3)}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Budget */}
                  <div>
                    <Label className="text-s-text text-sm font-medium mb-3 flex items-center gap-2">
                      <Wallet size={16} /> Budget Level
                    </Label>
                    <div className="grid grid-cols-3 gap-3">
                      {budgetOptions.map((opt) => (
                        <button
                          key={opt.id}
                          onClick={() => updateFormData('budget', opt.id)}
                          className={`p-4 rounded-xl border-2 transition-all text-center ${
                            formData.budget === opt.id
                              ? 'border-s-primary bg-s-primary/10'
                              : 'border-s-border bg-s-card hover:border-s-primary/50'
                          }`}
                        >
                          <div className="text-2xl mb-1">{opt.icon}</div>
                          <p className="text-s-text font-semibold text-sm">{opt.label}</p>
                          <p className="text-s-muted text-xs">{opt.description}</p>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Travel Style */}
                  <div>
                    <Label className="text-s-text text-sm font-medium mb-3 block">Travel Style</Label>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                      {travelStyles.map((style) => (
                        <button
                          key={style.id}
                          onClick={() => updateFormData('travel_type', style.id)}
                          className={`p-3 rounded-xl border transition-all ${
                            formData.travel_type === style.id
                              ? 'border-s-primary bg-s-primary/10'
                              : 'border-s-border bg-s-card hover:border-s-primary/50'
                          }`}
                        >
                          <p className="text-s-text font-semibold text-sm">{style.label}</p>
                          <p className="text-s-muted text-xs">{style.description}</p>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="flex gap-3 pt-2">
                    <Button
                      onClick={() => setCurrentStep(1)}
                      variant="outline"
                      className="flex-1 h-12 border-s-border text-s-text2"
                    >
                      <ArrowLeft className="mr-2" size={18} /> Back
                    </Button>
                    <Button
                      onClick={() => setCurrentStep(3)}
                      className="flex-1 h-12 bg-gradient-to-r from-s-primary to-s-primary hover:opacity-90 rounded-xl font-bold"
                      data-testid="step2-next-btn"
                    >
                      Next: Interests <ArrowRight className="ml-2" size={18} />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Step 3: Interests */}
            {currentStep === 3 && (
              <Card className="bg-s-bg2 border-s-border shadow-lg">
                <CardHeader className="border-b border-s-border pb-4">
                  <CardTitle className="flex items-center gap-2 text-xl md:text-2xl text-s-text">
                    <Sparkles className="w-5 h-5 md:w-6 md:h-6 text-s-primary" />
                    What do you enjoy most?
                  </CardTitle>
                  <p className="text-s-text2 text-sm mt-1">Select at least one interest</p>
                </CardHeader>
                <CardContent className="p-5 md:p-8 space-y-6">
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    {interests.map((interest) => (
                      <button
                        key={interest.id}
                        onClick={() => toggleInterest(interest.id)}
                        className={`relative p-4 rounded-2xl border-2 transition-all text-center ${
                          formData.interests.includes(interest.id)
                            ? 'border-s-primary bg-s-primary/10'
                            : 'border-s-border bg-s-card hover:border-s-primary/50'
                        }`}
                      >
                        {formData.interests.includes(interest.id) && (
                          <div className="absolute top-2 right-2 w-5 h-5 bg-s-primary rounded-full flex items-center justify-center">
                            <Check size={12} className="text-white" />
                          </div>
                        )}
                        <div className="text-3xl mb-2">{interest.icon}</div>
                        <p className="text-s-text font-semibold text-xs md:text-sm">{interest.label}</p>
                        <p className="text-s-muted text-[10px]">{interest.description}</p>
                      </button>
                    ))}
                  </div>

                  {/* Summary before generation */}
                  <div className="bg-s-card/50 rounded-xl p-4 border border-s-border">
                    <h4 className="text-s-text font-semibold mb-2">Trip Summary</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                      <div><span className="text-s-muted">Destination:</span> <span className="text-s-text">{formData.destination}</span></div>
                      <div><span className="text-s-muted">Duration:</span> <span className="text-s-text">{formData.duration} days</span></div>
                      <div><span className="text-s-muted">Travelers:</span> <span className="text-s-text">{formData.adults}A + {formData.children}C</span></div>
                      <div><span className="text-s-muted">Budget:</span> <span className="text-s-text capitalize">{formData.budget}</span></div>
                    </div>
                  </div>

                  <div className="flex gap-3 pt-2">
                    <Button
                      onClick={() => setCurrentStep(2)}
                      variant="outline"
                      className="flex-1 h-12 border-s-border text-s-text2"
                    >
                      <ArrowLeft className="mr-2" size={18} /> Back
                    </Button>
                    <Button
                      onClick={handleGeneratePlan}
                      disabled={!canProceedStep3 || isLoading}
                      className="flex-1 h-12 md:h-14 text-base bg-gradient-to-r from-s-primary to-s-primary hover:opacity-90 rounded-xl font-bold"
                      data-testid="generate-plan-btn"
                    >
                      {isLoading ? (
                        <><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Creating Your Plan...</>
                      ) : (
                        <><Sparkles className="mr-2 h-5 w-5" /> Generate My Itinerary</>
                      )}
                    </Button>
                  </div>
                  <p className="text-center text-s-muted text-xs">
                    Powered by AI • {formData.interests.length} interest{formData.interests.length !== 1 ? 's' : ''} selected
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        ) : aiResponse && (
          <div className="space-y-6">
            {/* Check if this is a fallback contact response */}
            {aiResponse.is_fallback_contact ? (
              <Card className="bg-s-bg2 border-s-border shadow-lg">
                <CardContent className="p-8 md:p-12 text-center">
                  <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-s-primary to-s-primary rounded-full flex items-center justify-center">
                    <MapPin className="w-10 h-10 text-white" />
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold text-s-text mb-4 font-heritage">
                    Let Us Plan Your Trip to {aiResponse.destination}
                  </h2>
                  <p className="text-s-text2 text-base md:text-lg mb-6 max-w-2xl mx-auto">
                    {aiResponse.fallback_message || `For a personalized ${aiResponse.duration}-day itinerary to ${aiResponse.destination}, please contact our travel experts.`}
                  </p>
                  
                  {/* Weather info if available */}
                  {aiResponse.weather?.current && (
                    <div className="bg-gradient-to-r from-s-primary/20 to-s-primary/20 rounded-xl p-4 mb-6 inline-flex items-center gap-4 border border-s-primary/30">
                      <Thermometer className="w-6 h-6 text-s-primary" />
                      <div className="text-left">
                        <p className="text-xs text-s-muted">Current Weather in {aiResponse.destination}</p>
                        <p className="text-s-text font-semibold">{Math.round(aiResponse.weather.current.temperature || 0)}°C</p>
                      </div>
                    </div>
                  )}

                  <div className="flex flex-col sm:flex-row justify-center gap-4 mt-6">
                    <Button 
                      onClick={() => {
                        const subject = `Trip Inquiry - ${aiResponse.destination} (${aiResponse.duration} days)`;
                        const message = `Hi,\n\nI'm interested in planning a ${aiResponse.duration}-day trip to ${aiResponse.destination}.\n\nTravelers: ${formData.adults} adults${formData.children > 0 ? `, ${formData.children} children` : ''}\nBudget: ${formData.budget}\nTravel Style: ${formData.travel_type}\nTravel Month: ${formData.travel_month || 'Flexible'}\nInterests: ${formData.interests.join(', ')}\n\nPlease help me plan this trip.\n\nThank you!`;
                        window.location.href = `/contact?message=${encodeURIComponent(message)}&subject=${encodeURIComponent(subject)}`;
                      }}
                      size="lg" 
                      className="bg-gradient-to-r from-s-primary to-s-primary hover:opacity-90 font-bold rounded-full h-14 px-8"
                      data-testid="contact-experts-btn"
                    >
                      <Share2 size={18} className="mr-2" /> Contact Our Travel Experts
                    </Button>
                    <Button 
                      onClick={handleReset} 
                      variant="outline" 
                      size="lg"
                      className="border-s-border text-s-text2 hover:bg-s-card rounded-full h-14 px-8"
                    >
                      <RefreshCw size={18} className="mr-2" /> Try Another Destination
                    </Button>
                  </div>

                  <div className="mt-8 pt-6 border-t border-s-border">
                    <p className="text-s-muted text-sm mb-3">Or reach us directly:</p>
                    <div className="flex flex-wrap justify-center gap-4 text-sm">
                      <a href="tel:+919876543210" className="flex items-center gap-2 text-s-text2 hover:text-s-primary transition-colors">
                        <span className="w-8 h-8 bg-s-card rounded-full flex items-center justify-center">📞</span>
                        +91 98765 43210
                      </a>
                      <a href="mailto:tours@sentoraholidays.com" className="flex items-center gap-2 text-s-text2 hover:text-s-primary transition-colors">
                        <span className="w-8 h-8 bg-s-card rounded-full flex items-center justify-center">✉️</span>
                        tours@sentoraholidays.com
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ) : (
            /* Regular AI Response Display */
            <>
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 bg-s-bg2 rounded-2xl p-5 md:p-6 border border-s-border">
              <div>
                <Badge className="bg-s-primary/20 text-s-primary border-s-primary/30 mb-2">
                  <CheckCircle size={14} className="mr-1" /> Plan Generated
                </Badge>
                <h2 className="text-2xl md:text-3xl font-bold text-s-text font-heritage">
                  Your Trip to {aiResponse.destination}
                </h2>
                <p className="text-s-text2 mt-1 text-sm">
                  {aiResponse.duration} days • {formData.adults} adults{formData.children > 0 ? `, ${formData.children} children` : ''} • {formData.budget} budget
                </p>
                {aiResponse.matched_package?.title && (
                  <p className="text-s-muted text-xs mt-1">
                    Based on: {aiResponse.matched_package.title}
                  </p>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                <Button onClick={handleDownloadItinerary} className="bg-s-primary hover:bg-s-primary/90" data-testid="download-plan-btn">
                  <Download size={16} className="mr-2" />Download
                </Button>
                <Button onClick={handleContactWithItinerary} variant="outline" className="border-s-primary text-s-primary hover:bg-s-primary/10">
                  <Share2 size={16} className="mr-2" />Book This
                </Button>
                <Button onClick={handleReset} variant="outline" className="border-s-border text-s-text2">
                  <RefreshCw size={16} className="mr-2" />New Plan
                </Button>
              </div>
            </div>

            {/* Transport Info */}
            {aiResponse.transport_info && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Card className="bg-s-bg2 border-s-border">
                  <CardContent className="p-4 flex items-center gap-3">
                    <Plane className="w-8 h-8 text-s-primary" />
                    <div>
                      <p className="text-xs text-s-muted">Nearest Airport</p>
                      <p className="text-sm text-s-text font-medium">{aiResponse.transport_info.nearest_airport || 'N/A'}</p>
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-s-bg2 border-s-border">
                  <CardContent className="p-4 flex items-center gap-3">
                    <Train className="w-8 h-8 text-s-primary" />
                    <div>
                      <p className="text-xs text-s-muted">Nearest Railway</p>
                      <p className="text-sm text-s-text font-medium">{aiResponse.transport_info.nearest_railway || 'N/A'}</p>
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-s-bg2 border-s-border">
                  <CardContent className="p-4 flex items-center gap-3">
                    <MapPinned className="w-8 h-8 text-s-primary" />
                    <div>
                      <p className="text-xs text-s-muted">Local Transport</p>
                      <p className="text-sm text-s-text font-medium">{aiResponse.transport_info.local_transport || 'N/A'}</p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Weather */}
            {aiResponse.weather && (
              <Card className="bg-gradient-to-r from-s-primary to-s-primary border-0">
                <CardContent className="p-5 md:p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <Sun className="w-5 h-5 text-s-primary" />
                    <h3 className="text-lg font-bold text-white">Current Weather</h3>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="flex items-center gap-3 bg-white/10 rounded-xl p-3">
                      <Thermometer className="w-8 h-8 text-white/90" />
                      <div>
                        <p className="text-xs text-white/70">Temperature</p>
                        <p className="text-xl font-bold text-white">{Math.round(aiResponse.weather.current?.temperature || 0)}°C</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 bg-white/10 rounded-xl p-3">
                      <Cloud className="w-8 h-8 text-white/90" />
                      <div>
                        <p className="text-xs text-white/70">Condition</p>
                        <p className="text-sm font-semibold text-white">{getWeatherDescription(aiResponse.weather.current?.weather_code)}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 bg-white/10 rounded-xl p-3">
                      <Wind className="w-8 h-8 text-white/90" />
                      <div>
                        <p className="text-xs text-white/70">Wind</p>
                        <p className="text-lg font-semibold text-white">{Math.round(aiResponse.weather.current?.wind_speed || 0)} km/h</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 bg-white/10 rounded-xl p-3">
                      <Globe className="w-8 h-8 text-white/90" />
                      <div>
                        <p className="text-xs text-white/70">Humidity</p>
                        <p className="text-lg font-semibold text-white">{aiResponse.weather.current?.humidity || 0}%</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Best Time & Family Notes */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {aiResponse.best_time_to_visit && (
                <Card className="bg-s-accent/10 border-s-accent/30">
                  <CardContent className="p-4 flex items-start gap-3">
                    <Clock className="w-6 h-6 text-s-accent flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="text-base font-bold text-s-text mb-1">Best Time to Visit</h4>
                      <p className="text-s-text2 text-sm">{aiResponse.best_time_to_visit}</p>
                    </div>
                  </CardContent>
                </Card>
              )}
              {aiResponse.family_notes && formData.children > 0 && (
                <Card className="bg-s-primary/10 border-s-primary/30">
                  <CardContent className="p-4 flex items-start gap-3">
                    <Baby className="w-6 h-6 text-s-primary flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="text-base font-bold text-s-text mb-1">Family & Child Notes</h4>
                      <p className="text-s-text2 text-sm">{aiResponse.family_notes}</p>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Tabs */}
            <Tabs defaultValue="itinerary" className="w-full">
              <TabsList className="grid w-full grid-cols-5 h-11 bg-s-card/50 rounded-xl">
                <TabsTrigger value="itinerary" className="text-xs data-[state=active]:bg-s-primary data-[state=active]:text-white rounded-lg">Itinerary</TabsTrigger>
                <TabsTrigger value="hotels" className="text-xs data-[state=active]:bg-s-primary data-[state=active]:text-white rounded-lg">Hotels</TabsTrigger>
                <TabsTrigger value="food" className="text-xs data-[state=active]:bg-s-primary data-[state=active]:text-white rounded-lg">Food</TabsTrigger>
                <TabsTrigger value="packing" className="text-xs data-[state=active]:bg-s-primary data-[state=active]:text-white rounded-lg">Packing</TabsTrigger>
                <TabsTrigger value="tips" className="text-xs data-[state=active]:bg-s-primary data-[state=active]:text-white rounded-lg">Tips</TabsTrigger>
              </TabsList>

              {/* Itinerary Tab */}
              <TabsContent value="itinerary" className="mt-4">
                <div className="space-y-3">
                  {aiResponse.itinerary?.map((day, idx) => (
                    <Card key={idx} className="bg-s-bg2 border-s-border overflow-hidden hover:border-s-primary/30 transition-all">
                      <div className="flex flex-col md:flex-row">
                        <div className="w-full md:w-20 bg-gradient-to-br from-s-primary to-s-primary flex flex-row md:flex-col items-center justify-center p-3 md:p-4 gap-2 md:gap-0">
                          <span className="text-2xl md:text-3xl font-bold text-white font-heritage">{day.day}</span>
                          <span className="text-xs text-white/80">Day</span>
                        </div>
                        <CardContent className="flex-1 p-4">
                          <h4 className="font-bold text-s-text text-base mb-3">{day.title}</h4>
                          <div className="space-y-2">
                            {day.activities?.map((activity, i) => (
                              <div key={i} className="flex items-start gap-2">
                                <CheckCircle size={14} className="text-s-primary flex-shrink-0 mt-1" />
                                <span className="text-s-text2 text-sm">{activity}</span>
                              </div>
                            ))}
                          </div>
                          {(day.meals || day.travel_time) && (
                            <div className="mt-3 pt-3 border-t border-s-border flex flex-wrap gap-4 text-xs">
                              {day.meals && (
                                <span className="text-s-muted flex items-center gap-1">
                                  <Utensils size={12} /> {day.meals}
                                </span>
                              )}
                              {day.travel_time && (
                                <span className="text-s-muted flex items-center gap-1">
                                  <Clock size={12} /> {day.travel_time}
                                </span>
                              )}
                            </div>
                          )}
                        </CardContent>
                      </div>
                    </Card>
                  ))}
                </div>

                {/* Top Attractions */}
                {aiResponse.top_attractions?.length > 0 && (
                  <Card className="bg-s-bg2 border-s-border mt-4">
                    <CardHeader className="pb-2">
                      <CardTitle className="flex items-center gap-2 text-base text-s-text">
                        <MapPin className="w-4 h-4 text-s-primary" /> Top Attractions
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <div className="flex flex-wrap gap-2">
                        {aiResponse.top_attractions.map((attr, idx) => (
                          <Badge key={idx} className="bg-s-primary/20 text-s-primary border-s-primary/30 text-xs py-1">
                            {attr}
                          </Badge>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>

              {/* Hotels Tab */}
              <TabsContent value="hotels" className="mt-4">
                <div className="space-y-4">
                  {aiResponse.hotel_suggestions?.budget?.length > 0 && (
                    <Card className="bg-s-bg2 border-s-border">
                      <CardHeader className="pb-2">
                        <CardTitle className="flex items-center gap-2 text-base text-s-text">
                          💰 Budget Hotels
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="pt-0">
                        <div className="space-y-2">
                          {aiResponse.hotel_suggestions.budget.map((hotel, idx) => (
                            <div key={idx} className="flex items-start gap-2 bg-s-card/50 p-3 rounded-lg">
                              <Hotel size={16} className="text-s-primary mt-0.5" />
                              <span className="text-s-text2 text-sm">{hotel}</span>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}
                  {aiResponse.hotel_suggestions?.midrange?.length > 0 && (
                    <Card className="bg-s-bg2 border-s-border">
                      <CardHeader className="pb-2">
                        <CardTitle className="flex items-center gap-2 text-base text-s-text">
                          💎 Mid-Range Hotels
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="pt-0">
                        <div className="space-y-2">
                          {aiResponse.hotel_suggestions.midrange.map((hotel, idx) => (
                            <div key={idx} className="flex items-start gap-2 bg-s-card/50 p-3 rounded-lg">
                              <Hotel size={16} className="text-s-accent mt-0.5" />
                              <span className="text-s-text2 text-sm">{hotel}</span>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}
                  {aiResponse.hotel_suggestions?.premium?.length > 0 && (
                    <Card className="bg-s-bg2 border-s-border">
                      <CardHeader className="pb-2">
                        <CardTitle className="flex items-center gap-2 text-base text-s-text">
                          👑 Premium Hotels
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="pt-0">
                        <div className="space-y-2">
                          {aiResponse.hotel_suggestions.premium.map((hotel, idx) => (
                            <div key={idx} className="flex items-start gap-2 bg-s-card/50 p-3 rounded-lg">
                              <Hotel size={16} className="text-s-primary mt-0.5" />
                              <span className="text-s-text2 text-sm">{hotel}</span>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>
              </TabsContent>

              {/* Food Tab */}
              <TabsContent value="food" className="mt-4">
                <Card className="bg-s-bg2 border-s-border">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-s-text">
                      <UtensilsCrossed className="w-5 h-5 text-s-primary" />
                      Local Food to Try
                    </CardTitle>
                    <p className="text-s-muted text-sm">Vegetarian-friendly regional specialties</p>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {aiResponse.local_food?.map((food, idx) => (
                        <div key={idx} className="flex items-center gap-3 bg-s-card/50 p-3 rounded-xl hover:bg-s-card transition-all">
                          <span className="text-xl">🍽️</span>
                          <span className="text-s-text2 text-sm">{food}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Packing Tab */}
              <TabsContent value="packing" className="mt-4">
                <Card className="bg-s-bg2 border-s-border">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-s-text">
                      <Shirt className="w-5 h-5 text-s-primary" />
                      Packing Checklist
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {aiResponse.packing_list?.map((item, idx) => (
                        <div key={idx} className="flex items-center gap-3 bg-s-card/50 p-3 rounded-xl hover:bg-s-card transition-all">
                          <CheckCircle size={16} className="text-s-primary flex-shrink-0" />
                          <span className="text-s-text2 text-sm">{item}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Tips Tab */}
              <TabsContent value="tips" className="mt-4">
                <div className="space-y-4">
                  <Card className="bg-s-bg2 border-s-border">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-s-text">
                        <AlertTriangle className="w-5 h-5 text-s-primary" />
                        Travel Tips
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {aiResponse.travel_tips?.map((tip, idx) => (
                          <div key={idx} className="flex items-start gap-3 bg-s-card/50 p-3 rounded-xl">
                            <Sparkles size={16} className="text-s-accent flex-shrink-0 mt-0.5" />
                            <span className="text-s-text2 text-sm">{tip}</span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="bg-s-bg2 border-s-border">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-s-text">
                        <Globe className="w-5 h-5 text-s-primary" />
                        Local Customs & Etiquette
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {aiResponse.local_customs?.map((custom, idx) => (
                          <div key={idx} className="flex items-start gap-3 bg-s-card/50 p-3 rounded-xl">
                            <CheckCircle size={16} className="text-s-primary flex-shrink-0 mt-0.5" />
                            <span className="text-s-text2 text-sm">{custom}</span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>
            </Tabs>

            {/* CTA */}
            <Card className="bg-gradient-to-r from-s-primary to-s-primary border-0">
              <CardContent className="p-6 md:p-8 text-center">
                <h3 className="text-xl md:text-2xl font-bold text-white mb-2 font-heritage">
                  Ready to Experience This Journey?
                </h3>
                <p className="text-white/90 mb-5 text-sm max-w-2xl mx-auto">
                  Let Sentora Holidays' experience curators handle everything - hotels, transport, guides. 40 years of crafting unforgettable travels.
                </p>
                <div className="flex flex-col sm:flex-row justify-center gap-3">
                  <Button onClick={handleContactWithItinerary} size="lg" className="bg-white text-s-primary hover:bg-gray-100 font-bold rounded-full h-12 px-6">
                    <Share2 size={16} className="mr-2" /> Book This Trip
                  </Button>
                  <Button onClick={handleDownloadItinerary} size="lg" variant="outline" className="border-white text-white hover:bg-white/10 rounded-full h-12 px-6">
                    <Download size={16} className="mr-2" /> Download Plan
                  </Button>
                </div>
              </CardContent>
            </Card>
            </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AIPlannerPage;
